import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Stethoscope, Scissors, FlaskConical, Ribbon, FileText, Shield, Lock, UserCheck, ArrowRight } from 'lucide-react';
import PageWrapper from '../components/PageWrapper';
import UnifiedSearch from '../components/UnifiedSearch';

const HomePage = () => {
  const navigate = useNavigate();

  const trustSignals = [
    {
      icon: UserCheck,
      title: "Physician-Created",
      description: "Content developed by board-certified urologists"
    },
    {
      icon: Shield,
      title: "Education Only",
      description: "Not medical advice — supports informed conversations"
    },
    {
      icon: Lock,
      title: "Private & Secure",
      description: "No ads, no tracking, no data selling"
    }
  ];

  // Six Hub Quick Links
  const hubLinks = [
    {
      icon: Activity,
      title: "Symptoms",
      description: "Understand what your symptoms mean",
      path: "/symptoms",
      color: "#ef4444"
    },
    {
      icon: Stethoscope,
      title: "Conditions",
      description: "Learn about urologic conditions",
      path: "/conditions",
      color: "#0d9488"
    },
    {
      icon: Scissors,
      title: "Procedures",
      description: "Surgical treatments and what to expect",
      path: "/procedures",
      color: "#8b5cf6"
    },
    {
      icon: FlaskConical,
      title: "Tests",
      description: "Diagnostic tests explained",
      path: "/tests",
      color: "#f59e0b"
    },
    {
      icon: Ribbon,
      title: "Cancers",
      description: "Cancer screening and treatment",
      path: "/cancers",
      color: "#ec4899"
    },
    {
      icon: FileText,
      title: "After Visit",
      description: "Post-procedure care instructions",
      path: "/after-visit",
      color: "#3b82f6"
    }
  ];

  return (
    <PageWrapper>
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">Uro2Go</h1>
          <p className="hero-subtitle">Clear, trusted urology education — wherever you are.</p>
          <p className="hero-description">
            Understand your condition, your testing, and your treatment options with 
            easy-to-follow explanations created by board-certified urologists.
          </p>
          <div className="hero-buttons">
            <button 
              className="btn-primary-hero"
              onClick={() => navigate('/conditions')}
              data-testid="explore-topics-btn"
            >
              Explore Topics
              <ArrowRight size={18} />
            </button>
            <button 
              className="btn-secondary-hero"
              onClick={() => navigate('/symptoms')}
              data-testid="symptoms-btn"
            >
              Browse Symptoms
            </button>
          </div>
          
          {/* Unified Search */}
          <div className="hero-search">
            <UnifiedSearch 
              placeholder="Search topics, videos, and guides..." 
              variant="hero" 
            />
          </div>
        </div>
      </section>

      {/* Trust Signals Section */}
      <section className="trust-section" data-testid="trust-section">
        <div className="trust-grid">
          {trustSignals.map((signal, index) => (
            <div key={index} className="trust-card" data-testid={`trust-card-${index}`}>
              <div className="trust-icon">
                <signal.icon size={28} />
              </div>
              <h3>{signal.title}</h3>
              <p>{signal.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features Section - Six Hubs */}
      <section className="features-section" data-testid="features-section">
        <h2 className="section-title">Explore by Category</h2>
        <div className="hub-links-grid">
          {hubLinks.map((hub, index) => (
            <button 
              key={index}
              className="hub-link-card"
              onClick={() => navigate(hub.path)}
              data-testid={`hub-link-${hub.title.toLowerCase()}`}
            >
              <div className="hub-link-icon" style={{ backgroundColor: `${hub.color}15`, color: hub.color }}>
                <hub.icon size={28} />
              </div>
              <div className="hub-link-content">
                <h3>{hub.title}</h3>
                <p>{hub.description}</p>
              </div>
              <ArrowRight size={18} className="hub-link-arrow" />
            </button>
          ))}
        </div>
      </section>

      {/* Reassurance Section */}
      <section className="reassurance-section" data-testid="reassurance-section">
        <div className="reassurance-content">
          <p>
            "Uro2Go is designed to support conversations with your urologist — not replace them."
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" data-testid="cta-section">
        <button 
          className="btn-cta"
          onClick={() => navigate('/conditions')}
          data-testid="start-learning-btn"
        >
          Browse All Conditions
          <ArrowRight size={20} />
        </button>
      </section>
    </PageWrapper>
  );
};

export default HomePage;
